(() => {
  if (customElements.get('luminova-customer-media')) return;

  class LuminovaCustomerMedia extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.items = [...this.querySelectorAll('[data-lncm-item]')];
      this.viewer = this.querySelector('[data-lncm-viewer]');
      this.viewerImage = this.querySelector('[data-lncm-viewer-image]');
      this.viewerVideo = this.querySelector('[data-lncm-viewer-video]');
      this.viewerToggle = this.querySelector('[data-lncm-viewer-toggle]');
      this.status = this.querySelector('[data-lncm-status]');
      this.currentIndex = 0;
      this.lastTrigger = null;

      this.querySelectorAll('video').forEach((video) => {
        video.controls = false;
        video.removeAttribute('controls');
      });

      this.addEventListener('click', (event) => this.onClick(event), { signal: this.abortController.signal });
      document.addEventListener('keydown', (event) => this.onKeydown(event), { signal: this.abortController.signal });

      if (this.viewerVideo) {
        this.viewerVideo.addEventListener('play', () => this.updateVideoToggle(), { signal: this.abortController.signal });
        this.viewerVideo.addEventListener('pause', () => this.updateVideoToggle(), { signal: this.abortController.signal });
        this.viewerVideo.addEventListener('ended', () => this.updateVideoToggle(), { signal: this.abortController.signal });
      }

      const nav = this.querySelector('.ln-customer-media__viewer-nav');
      if (nav) nav.hidden = this.items.length < 2;
    }

    disconnectedCallback() {
      this.closeViewer(false);
      this.abortController?.abort();
    }

    onClick(event) {
      const openButton = event.target.closest?.('[data-lncm-open]');
      if (openButton && this.contains(openButton)) {
        const item = openButton.closest('[data-lncm-item]');
        const index = this.items.indexOf(item);
        if (index >= 0) this.openViewer(index, openButton);
        return;
      }

      if (!this.viewer || this.viewer.hidden) return;
      if (event.target.closest?.('[data-lncm-close]')) this.closeViewer();
      else if (event.target.closest?.('[data-lncm-previous]')) this.showAdjacent(-1);
      else if (event.target.closest?.('[data-lncm-next]')) this.showAdjacent(1);
      else if (event.target.closest?.('[data-lncm-viewer-toggle]') || event.target === this.viewerVideo) this.toggleVideo();
      else if (event.target === this.viewer) this.closeViewer();
    }

    onKeydown(event) {
      if (!this.viewer || this.viewer.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeViewer();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.showAdjacent(-1);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.showAdjacent(1);
      } else if (event.key === 'Tab') {
        this.trapFocus(event);
      }
    }

    openViewer(index, trigger) {
      if (!this.viewer || !this.items.length) return;
      this.lastTrigger = trigger;
      this.viewer.hidden = false;
      document.documentElement.classList.add('lncm-viewer-open');
      this.showMedia(index);
      requestAnimationFrame(() => this.querySelector('[data-lncm-close]')?.focus());
    }

    closeViewer(restoreFocus = true) {
      if (!this.viewer || this.viewer.hidden) return;
      this.stopViewerVideo();
      this.viewer.hidden = true;
      document.documentElement.classList.remove('lncm-viewer-open');
      if (restoreFocus && this.lastTrigger?.isConnected) this.lastTrigger.focus();
    }

    showAdjacent(direction) {
      if (!this.items.length) return;
      this.showMedia((this.currentIndex + direction + this.items.length) % this.items.length);
    }

    showMedia(index) {
      const item = this.items[index];
      if (!item || !this.viewerImage || !this.viewerVideo) return;
      this.currentIndex = index;
      this.stopViewerVideo();

      const kind = item.dataset.lncmKind;
      const label = item.dataset.lncmLabel || (kind === 'video' ? 'Customer video' : 'Customer image');
      if (this.status) this.status.textContent = `${index + 1} of ${this.items.length}: ${label}`;

      if (kind === 'video') {
        const thumbnailVideo = item.querySelector('video');
        this.viewerImage.hidden = true;
        this.viewerImage.removeAttribute('src');
        this.viewerVideo.hidden = false;
        this.viewerVideo.controls = false;
        this.viewerVideo.removeAttribute('controls');
        this.viewerVideo.poster = thumbnailVideo?.poster || '';
        this.copyVideoSources(thumbnailVideo, this.viewerVideo);
        this.viewerVideo.load();
        this.playViewerVideo();
      } else {
        this.viewerVideo.hidden = true;
        this.viewerToggle.hidden = true;
        this.viewerImage.src = item.dataset.lncmImageSrc || item.querySelector('img')?.currentSrc || item.querySelector('img')?.src || '';
        this.viewerImage.alt = item.dataset.lncmImageAlt || label;
        this.viewerImage.hidden = false;
      }
    }

    copyVideoSources(sourceVideo, targetVideo) {
      targetVideo.removeAttribute('src');
      targetVideo.replaceChildren();
      if (!sourceVideo) return;
      const sources = sourceVideo.querySelectorAll('source');
      if (sources.length) sources.forEach((source) => targetVideo.append(source.cloneNode(true)));
      else if (sourceVideo.currentSrc || sourceVideo.src) targetVideo.src = sourceVideo.currentSrc || sourceVideo.src;
    }

    playViewerVideo() {
      if (!this.viewerVideo || this.viewerVideo.hidden) return;
      this.viewerVideo.controls = false;
      this.viewerVideo.removeAttribute('controls');
      const playback = this.viewerVideo.play();
      if (playback?.catch) playback.catch(() => this.updateVideoToggle(true));
      this.updateVideoToggle();
    }

    toggleVideo() {
      if (!this.viewerVideo || this.viewerVideo.hidden) return;
      if (this.viewerVideo.paused || this.viewerVideo.ended) {
        if (this.viewerVideo.ended) this.viewerVideo.currentTime = 0;
        this.playViewerVideo();
      } else {
        this.viewerVideo.pause();
      }
    }

    updateVideoToggle(forceVisible = false) {
      if (!this.viewerToggle || !this.viewerVideo) return;
      const paused = forceVisible || this.viewerVideo.paused || this.viewerVideo.ended;
      this.viewerToggle.hidden = this.viewerVideo.hidden || !paused;
      this.viewerToggle.setAttribute('aria-label', this.viewerVideo.ended ? 'Replay video' : 'Play video');
    }

    stopViewerVideo() {
      if (!this.viewerVideo) return;
      this.viewerVideo.pause();
      this.viewerVideo.removeAttribute('src');
      this.viewerVideo.replaceChildren();
      this.viewerVideo.load();
      if (this.viewerToggle) this.viewerToggle.hidden = true;
    }

    trapFocus(event) {
      const controls = [...this.viewer.querySelectorAll('button:not([hidden])')].filter((button) => !button.disabled);
      if (!controls.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('luminova-customer-media', LuminovaCustomerMedia);
})();
